function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
import { addBreadcrumb, getCurrentHub, startTransaction } from "@sentry/core";
import { AxiosError } from "axios";
import HttpClient from "../../http-client";
import { AxiosGraphQLError } from "../../utils/AxiosGraphQLError";
import { isGraphQLQuery } from "../utils/graphql";
/**
 * Generates the transaction name based on the request configuration.
 * @param {InternalAxiosRequestConfig} config
 */ var getTransactionName = function(config) {
    var method = config.method ? config.method.toUpperCase() : "UNKNOWN";
    return method + " " + config.baseURL + config.url;
};
/**
 * Gets the Query name from the GraphQL query.
 * Uses string manipulation to extract the query name from the query.
 * @param {InternalAxiosRequestConfig} config
 */ var getGraphQLQueryName = function(config) {
    var queryHeader = config.data.query.split("{")[0].trim(); // gets the query queryName(variables)
    var queryName = queryHeader.split(" ")[1];
    if (queryName) {
        if (queryName.endsWith("(")) return queryName.slice(0, -1); // removes the last ) from the queryName
        return queryName;
    }
    return "unknown";
};
export var getTransactionFromResponse = function(response) {
    return response.config.transaction;
};
export var getTransactionFromError = function(err) {
    return err.config.transaction;
};
export var getErrorsFromGraphQL = function(response, transaction) {
    var _response_data = response.data, data = _response_data === void 0 ? {} : _response_data;
    // Set errors context for GQL calls.
    if (data.errors && data.errors.length > 0) {
        data.errors.forEach(function(error, i) {
            transaction.setContext("Error Context (" + i + ")", error);
        });
    }
};
/**
 * Sets the performance monitoring for the request using Sentry.
 * Starts a transaction and adds the request context to the transaction.
 * The transaction is then added to the request configuration, so it can be finished later in the response.
 * @param {HttpClient} httpClient
 */ function setReqPerformanceMonitoring(httpClient) {
    return function(config) {
        try {
            var _headers_cookie;
            var transaction = startTransaction({
                name: getTransactionName(config),
                op: "http.client",
                description: httpClient.clientName
            });
            addBreadcrumb({
                type: "debug",
                level: "info",
                message: "lll-http-client: Adding Transaction Context",
                data: {
                    client: httpClient.clientName,
                    clientVersion: HttpClient.clientVersion,
                    baseURL: config.baseURL,
                    url: config.url,
                    method: config.method
                }
            });
            var headers = config.headers || {};
            transaction.setContext("Request", {
                method: config.method,
                url: config.url,
                baseURL: config.baseURL,
                headers: headers.toJSON(),
                cookieSize: (_headers_cookie = headers["cookie"]) == null ? void 0 : _headers_cookie.length,
                headerSize: headers ? JSON.stringify(headers).length : 0,
                contentLength: headers["content-length"],
                cacheControl: headers["cache-control"]
            });
            if (isGraphQLQuery(config)) {
                addBreadcrumb({
                    type: "debug",
                    level: "info",
                    message: "lll-http-client: Adding GraphQL Context",
                    data: {
                        queryName: getGraphQLQueryName(config)
                    }
                });
                transaction.setName("GraphQL " + getGraphQLQueryName(config));
                transaction.setTag("queryName", getGraphQLQueryName(config));
                transaction.setContext("GraphQL Variables", config.data.variables);
            }
            config.headers["sentry-trace"] = transaction.traceId;
            return _extends({}, config, {
                transaction: transaction
            });
        } catch (error) {
            console.error(error);
            return config;
        }
    };
}
/**
 * Interceptor to set up the performance monitoring with Sentry for the request.
 * @param httpClient The http client instance.
 * @param options Options for the interceptor.
 * @returns Request Interceptor Number.
 */ export var setReqSentryPerformanceMonitoring = function(httpClient, options) {
    if (options === void 0) options = {};
    var _options_synchronous = options.synchronous, synchronous = _options_synchronous === void 0 ? true : _options_synchronous, _options_runWhen = options.runWhen, runWhen = _options_runWhen === void 0 ? function() {
        return getCurrentHub() !== undefined;
    } : _options_runWhen, _options_onSuccess = options.onSuccess, onSuccess = _options_onSuccess === void 0 ? setReqPerformanceMonitoring(httpClient) : _options_onSuccess, _options_onError = options.onError, onError = _options_onError === void 0 ? function(err) {
        return err;
    } : _options_onError;
    return httpClient.axios.interceptors.request.use(onSuccess, onError, {
        synchronous: synchronous,
        runWhen: runWhen
    });
};
/**
 * Set the performance monitoring for the response. This function is called when the response is received.
 * It uses the transaction attached to the request to set the response status, headers, and content length.
 * Also adds the errors context for GQL calls.
 * This finishes the transaction.
 * @param httpClient The HttpClient instance.
 */ function setResPerformanceMonitoring(httpClient) {
    return function(response) {
        addBreadcrumb({
            type: "debug",
            level: "info",
            message: "lll-http-client: Finish Response Interceptor",
            data: {
                responseStatus: response.status,
                clientName: httpClient.clientName,
                clientVersion: HttpClient.clientVersion,
                baseURL: response.config.baseURL,
                url: response.config.url,
                method: response.config.method
            }
        });
        var transaction = getTransactionFromResponse(response);
        if (transaction) {
            var _response_headers = response.headers, headers = _response_headers === void 0 ? {} : _response_headers;
            transaction.setContext("Response", {
                status: response.status,
                statusText: response.statusText,
                headers: headers,
                contentLength: headers["content-length"],
                cacheControl: headers["cache-control"]
            });
            getErrorsFromGraphQL(response, transaction);
            transaction.finish();
        }
        return response;
    };
}
function setResPerformanceMonitoringError(httpClient) {
    return function(error) {
        if (error instanceof AxiosError || error instanceof AxiosGraphQLError) {
            var transaction = getTransactionFromError(error);
            if (transaction) {
                if (error.response) {
                    var response = error.response;
                    var headers = response.headers;
                    addBreadcrumb({
                        type: "debug",
                        level: "info",
                        message: "lll-http-client: Finish Response Interceptor on Error",
                        data: {
                            responseStatus: response.status,
                            clientName: httpClient.clientName,
                            clientVersion: HttpClient.clientVersion,
                            baseURL: response.config.baseURL,
                            url: response.config.url,
                            method: response.config.method
                        }
                    });
                    getErrorsFromGraphQL(response, transaction);
                    transaction.setContext("Response", {
                        status: response.status,
                        statusText: response.statusText,
                        headers: headers,
                        contentLength: headers["content-length"],
                        cacheControl: headers["cache-control"]
                    });
                }
                transaction.finish();
            }
        }
        throw error;
    };
}
/**
 * Sets the response interceptor for performance monitoring.
 * This interceptor is called when the response is received.
 * It uses the transaction attached to the request to set the response status, headers, and content length.
 * @param httpClient The HttpClient instance.
 * @param options The options for the interceptor.
 */ export var setResSentryPerformanceMonitoring = function(httpClient, options) {
    if (options === void 0) options = {};
    var _options_onSuccess = options.onSuccess, onSuccess = _options_onSuccess === void 0 ? setResPerformanceMonitoring(httpClient) : _options_onSuccess, _options_onError = options.onError, onError = _options_onError === void 0 ? setResPerformanceMonitoringError(httpClient) : _options_onError;
    return httpClient.axios.interceptors.response.use(onSuccess, onError);
};
